/** @jsxImportSource @emotion/react */

import type { ComponentPropsWithRef, ElementType } from "react";
import { forwardRef } from "react";
import { Spinner } from "../Spinner";
import { buttonCSS } from "./Button.css";
import type { ButtonProps } from "./Button.types";

type ButtonComponent = <E extends ElementType = "button">(
  props: ButtonProps<E> & {
    ref?: ComponentPropsWithRef<E>["ref"];
  },
) => JSX.Element | null;

const Button: ButtonComponent = forwardRef(
  <E extends ElementType>(props: ButtonProps<E>, ref: ComponentPropsWithRef<E>["ref"]) => {
    const Element = props.as ?? "button";
    const { isLoading, isDisabled, leadingIcon, trailingIcon, text } = props;

    const isTextOnly = !leadingIcon && !trailingIcon;

    return (
      <Element
        ref={ref}
        data-imweb-loading={isLoading}
        disabled={isLoading || isDisabled}
        css={[buttonCSS(props), props.sx]}
        {...props}
        {...props.native}
      >
        {(leadingIcon || isTextOnly) && isLoading ? <Spinner /> : leadingIcon}
        {text}
        {trailingIcon && isLoading ? <Spinner /> : trailingIcon}
      </Element>
    );
  },
);

const ButtonNameSpace = Object.assign(Button, { displayName: "Clay.Button" });

export { ButtonNameSpace as Button };
